import { SystemEntity } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { usePurchaseOrderLineItem } from '@/api/rpc/hooks/accounts-payable/usePurchaseOrders';
import { ListChecksIcon, ArrowSquareOutIcon } from '@phosphor-icons/react';
import { formatDateOnlyString } from '@/utils/dateUtils';

interface PurchaseOrderLineItemPreviewProps {
  entityType: SystemEntity;
  entityId: string;
  className?: string;
}

// Helper function to format currency
const formatCurrencyValue = (
  value: string | undefined | null,
  currency = 'USD',
): string => {
  if (!value) return 'N/A';

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
};

export function PurchaseOrderLineItemPreview({
  entityId,
  className,
}: PurchaseOrderLineItemPreviewProps) {
  const { lineItem, purchaseOrder, isLoading, error } =
    usePurchaseOrderLineItem(entityId);

  if (!entityId) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <ListChecksIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">PO Line Item</h4>
        </div>
        <p className="text-xs text-muted-foreground">No entity ID provided</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <ListChecksIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">PO Line Item</h4>
        </div>
        <p className="text-xs text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (error || !lineItem || !purchaseOrder) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <ListChecksIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">PO Line Item</h4>
        </div>
        <p className="text-xs text-muted-foreground">
          Unable to load line item details
          {error && <span className="block mt-1">Error: {String(error)}</span>}
        </p>
      </div>
    );
  }

  const currency = purchaseOrder.currency || 'USD';

  return (
    <div className={cn('min-w-64 max-w-sm p-3', className)}>
      <div className="flex items-center gap-2 mb-3">
        <ListChecksIcon className="size-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold">Line {lineItem.lineNumber}</h4>
      </div>

      <div className="space-y-2 text-xs mb-3">
        <div>
          <span className="text-muted-foreground">PO: </span>
          <span className="font-medium">
            {purchaseOrder.externalId || `PO-${purchaseOrder.internalId}`}
          </span>
        </div>

        {lineItem.description && (
          <div>
            <span className="text-muted-foreground">Description: </span>
            <span className="font-medium">{lineItem.description}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          {lineItem.quantity !== null && lineItem.quantity !== undefined && (
            <div>
              <span className="text-muted-foreground">Quantity: </span>
              <span className="font-medium">
                {lineItem.quantity}
                {lineItem.uom ? ` ${lineItem.uom}` : ''}
              </span>
            </div>
          )}

          {lineItem.unitPrice && (
            <div>
              <span className="text-muted-foreground">Unit Price: </span>
              <span className="font-medium">
                {formatCurrencyValue(lineItem.unitPrice, currency)}
              </span>
            </div>
          )}
        </div>

        {lineItem.totalPrice && (
          <div>
            <span className="text-muted-foreground">Total: </span>
            <span className="font-medium">
              {formatCurrencyValue(lineItem.totalPrice, currency)}
            </span>
          </div>
        )}

        {lineItem.expectedDeliveryDate && (
          <div>
            <span className="text-muted-foreground">Expected Delivery: </span>
            <span className="font-medium">
              {formatDateOnlyString(lineItem.expectedDeliveryDate)}
            </span>
          </div>
        )}
      </div>

      <div className="mt-3">
        <a
          href={`/accounts-payable/purchase-orders/${purchaseOrder.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center w-full px-3 py-1.5 text-xs font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
        >
          <ArrowSquareOutIcon className="size-3 mr-1.5" />
          View Purchase Order
        </a>
      </div>
    </div>
  );
}
